import * as React from "react";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import * as SitesState from "../../store/Sites";
import { PageControl } from "./PageControl";
import { debounce } from "lodash";
import { defaultPageSize } from "../../helpers";
import { SiteResponse, SiteSystemType } from "../../api/ApiClient";
import { Spinner } from "./Spinner";
import { SiteSyncingState } from "../../store/Sites";
import { Link } from "react-router-dom";
import { useSiteBasePath } from "../../shared/utils/siteNavPath";
import SearchInput from "./SearchInput";
import { useSnackbar } from "notistack";
import { Button } from "components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";

interface Injected {
  sites: SitesState.SitesState;
}

type OwnProps = {
  onSelected?: (site: SiteResponse) => any;
  pageSize?: number;
  showFirst?: number | null | undefined;
  selectedSites?: Array<SiteResponse>;
  renderButton?: (site: SiteSyncingState) => any;
  selectedLabel?: string;
  selectedBtnClass?: string;
  unselectedLabel?: string;
  unselectedBtnClass?: string;
  btnStyle?: Object;
};

interface SnackProps {
  enqueueSnackbar?: (node: React.ReactNode, options?: any) => void;
  closeSnackbar?: (key?: any) => void;
}

type Props = Injected & typeof SitesState.actionCreators & OwnProps & SnackProps;

interface State {
  typedSite: string;
}

const SelectSite: React.FC<Props> = (props) => {
  const [typedSite, setTypedSite] = useState("");
  const prevPageSizeRef = useRef<number | undefined>(undefined);
  const siteBasePath = useSiteBasePath();

  const refresh = useCallback(async () => {
    try {
      await props.setFetchParamSites({
        page: 0,
        pageSize: props.pageSize || defaultPageSize,
        searchTerm: typedSite,
      });
    } catch (e) {
      console.error("Error requesting sites", e);
      const message = (e as any)?.message || "Error requesting sites";
      props.enqueueSnackbar &&
        props.enqueueSnackbar(
          <span style={{ fontSize: 12 }}>{message}</span>,
          { variant: "error" },
        );
    }
  }, [props, typedSite]);

  // Debounced search mirroring class-based behavior
  const searchSite = useMemo(() => debounce(refresh, 1000), [refresh]);

  const handleTypedSite = (value: string) => {
    setTypedSite(value);
    searchSite();
  };

  // componentDidMount equivalent
  useEffect(() => {
    console.log("SelectSite: Requesting sites...");
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // componentDidUpdate for pageSize changes only (avoid duplicate initial fetch)
  useEffect(() => {
    if (
      prevPageSizeRef.current !== undefined &&
      prevPageSizeRef.current !== props.pageSize
    ) {
      refresh();
    }
    prevPageSizeRef.current = props.pageSize;
  }, [props.pageSize, refresh]);

  // Clean up debounced function on unmount
  useEffect(() => {
    return () => {
      searchSite.cancel();
    };
  }, [searchSite]);

  const defaultRenderButton = useCallback(
    (site: SiteResponse) => {
      const {
        selectedSites,
        onSelected,
        btnStyle,
        selectedBtnClass,
        selectedLabel,
        unselectedBtnClass,
        unselectedLabel,
      } = props;
      const isSelected =
        selectedSites && selectedSites.find((s) => s.id === site.id);
      const className = isSelected
        ? selectedBtnClass || "btn-primary"
        : unselectedBtnClass || "btn-default";
      return (
        <Button
          className={`btn ${className}`}
          style={btnStyle as React.CSSProperties | undefined}
          onClick={() => onSelected && onSelected(site)}
          type="button"
          variant={isSelected ? "default" : "outline"}
        >
          {!isSelected
            ? unselectedLabel || "Select"
            : selectedLabel || "Unselect"}
        </Button>
      );
    },
    [props],
  );

  const renderSitesTable = (sites: SiteSyncingState[]) => {
    const { renderButton, showFirst } = props;
    const { isLoading } = props.sites;
    const list = !showFirst ? sites : (sites || []).slice(0, showFirst);

    return (
      <div>
        <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
          <Table
            aria-label="sites table"
            className="[&_th]:whitespace-normal [&_th]:text-xs [&_th]:font-semibold [&_th]:leading-[1.4] [&_td]:whitespace-normal [&_td]:text-xs [&_td]:leading-[1.4]"
          >
            <TableHeader>
              <TableRow>
                <TableHead>Id</TableHead>
                <TableHead>SystemSideId</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>SystemType</TableHead>
                <TableHead>PropertyExternalId</TableHead>
                <TableHead>Account</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((site, i) => (
                <TableRow key={site.id || `f_${i}`}>
                  <TableCell>{site.id}</TableCell>
                  <TableCell>{site.systemSiteId}</TableCell>
                  <TableCell>{site.name}</TableCell>
                  <TableCell>
                    {site.account ? site.account.systemType : ""}
                  </TableCell>
                  <TableCell>{site.propertyExternalId}</TableCell>
                  <TableCell>{site.account ? site.account.name : ""}</TableCell>
                  <TableCell className="min-w-35 text-right">
                    <div className="flex flex-col gap-2 w-full">
                      {renderButton
                        ? renderButton(site)
                        : defaultRenderButton(site)}
                      <Button asChild className="w-full">
                        <Link
                          to={`${siteBasePath}/${site.id}/guests?filterOperation=GreaterThanOrEqualTo&filterProperty=ValidTo`}
                        >
                          Guests
                        </Link>
                      </Button>
                      <Button asChild className="w-full">
                        <Link to={`${siteBasePath}/${site.id}/accessschedules`}>
                          Access Schedules
                        </Link>
                      </Button>
                      <Button asChild className="w-full">
                        <Link to={`${siteBasePath}/${site.id}/edit`}>
                          Edit Site
                        </Link>
                      </Button>
                      {site.account?.systemType ===
                        SiteSystemType.SchlageEngage && (
                        <>
                          <Button asChild className="w-full">
                            <Link to={`${siteBasePath}/${site.id}/keys`}>
                              Keys
                            </Link>
                          </Button>
                          <Button asChild className="w-full">
                            <Link
                              to={`${siteBasePath}/${site.id}/keys-audit`}
                              state={{ site }}
                            >
                              Audit
                            </Link>
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {!showFirst && (
          <div className="mt-4">
            <PageControl
              onNext={props.nextSitePage}
              onPrev={props.prevSitePage}
              currentPage={props.sites.page}
              isLoading={isLoading}
              disablePrev={props.sites.page === 0}
              disableNext={props.sites.sites.length < props.sites.pageSize}
            />
          </div>
        )}
      </div>
    );
  };

  const { sites, searchTerm, isLoading } = props.sites;

  return (
    <div>
      <div className="flex justify-end">
        <div className="w-96">
          <SearchInput
            name="typedSite"
            placeholder="search here"
            value={typedSite}
            onChange={(e) => handleTypedSite(e.target.value)}
          />
        </div>
      </div>
      <h4>
        {searchTerm ? `Sites Matching '${searchTerm}'` : ""}
        {isLoading && <Spinner />}
      </h4>
      {renderSitesTable(sites)}
    </div>
  );
};

const ConnectedSelectSite = connect<any, any, OwnProps, ApplicationState>(
  (state) => ({
    sites: state.sites,
  }),
  { ...SitesState.actionCreators },
)(SelectSite);

const SelectSiteWithSnackbar: React.FC<any> = (props) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  return (
    <ConnectedSelectSite
      {...props}
      enqueueSnackbar={enqueueSnackbar}
      closeSnackbar={closeSnackbar}
    />
  );
};

export default SelectSiteWithSnackbar;
